import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, X, Calendar, Clock, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { getHolidaysForYear } from "@shared/projectConstants";
import {
  useUpdateMilestone,
  useCreateMilestone,
  useUpdateCalendarNote,
} from "@/hooks/useProjects";
import type { ProjectFull, ProjectMilestone } from "@/hooks/useProjects";

interface CalendarViewProps {
  project: ProjectFull;
  clientFacing?: boolean;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Use UTC date string to prevent timezone shifts
// Dates are stored as UTC in the database — always compare in UTC
function toLocalDateStr(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateRange(dueDate: string, duration: number): string {
  const start = new Date(dueDate);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + duration - 1);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };
  if (duration <= 1) return start.toLocaleDateString("en-US", { ...opts, year: "numeric" });
  return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
}

const priorityColor = (p: string | null) => {
  if (p === "High") return "bg-red-500/20 text-red-500 border-red-500/30";
  if (p === "Medium") return "bg-amber-500/20 text-amber-600 border-amber-500/30 dark:text-amber-400";
  return "bg-slate-500/15 text-slate-500 border-slate-400/30";
};

// Identity-based color palette — each milestone gets a stable color derived
// from its ID so "Kickoff Meeting" is always teal, "Data Room" always indigo,
// etc. Priority remains a subtle side indicator, not the dominant signal.
// Classes are literal strings so Tailwind JIT picks them up.
const IDENTITY_PALETTE = [
  { bg: "bg-sky-500/15",     text: "text-sky-700 dark:text-sky-400",     border: "border-sky-500/40",     ring: "ring-sky-500/40"     },
  { bg: "bg-indigo-500/15",  text: "text-indigo-700 dark:text-indigo-400",  border: "border-indigo-500/40",  ring: "ring-indigo-500/40"  },
  { bg: "bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-400", border: "border-emerald-500/40", ring: "ring-emerald-500/40" },
  { bg: "bg-violet-500/15",  text: "text-violet-700 dark:text-violet-400",  border: "border-violet-500/40",  ring: "ring-violet-500/40"  },
  { bg: "bg-rose-500/15",    text: "text-rose-700 dark:text-rose-400",    border: "border-rose-500/40",    ring: "ring-rose-500/40"    },
  { bg: "bg-teal-500/15",    text: "text-teal-700 dark:text-teal-400",    border: "border-teal-500/40",    ring: "ring-teal-500/40"    },
  { bg: "bg-fuchsia-500/15", text: "text-fuchsia-700 dark:text-fuchsia-400", border: "border-fuchsia-500/40", ring: "ring-fuchsia-500/40" },
  { bg: "bg-lime-500/15",    text: "text-lime-700 dark:text-lime-400",    border: "border-lime-500/40",    ring: "ring-lime-500/40"    },
  { bg: "bg-cyan-500/15",    text: "text-cyan-700 dark:text-cyan-400",    border: "border-cyan-500/40",    ring: "ring-cyan-500/40"    },
  { bg: "bg-orange-500/15",  text: "text-orange-700 dark:text-orange-400",  border: "border-orange-500/40",  ring: "ring-orange-500/40"  },
];
function identityColor(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return IDENTITY_PALETTE[Math.abs(hash) % IDENTITY_PALETTE.length];
}

const priorityDot = (p: string | null) => {
  if (p === "High") return "bg-red-500";
  if (p === "Medium") return "bg-amber-500";
  return "bg-slate-400";
};

export function CalendarView({ project, clientFacing = false }: CalendarViewProps) {
  const { milestones, calendarNotes, teamMembers } = project;
  const startDate = new Date(project.startDate);

  const updateMilestone = useUpdateMilestone(project.id);
  const createMilestone = useCreateMilestone(project.id);
  const updateNote = useUpdateCalendarNote(project.id);

  const [detailModal, setDetailModal] = useState<ProjectMilestone | null>(null);
  const [tempNote, setTempNote] = useState("");
  const [localProgress, setLocalProgress] = useState<number | null>(null);
  const [newTaskDate, setNewTaskDate] = useState<Date | null>(null);
  const [newTaskName, setNewTaskName] = useState("");
  const [draggedId, setDraggedId] = useState<string | null>(null);
  // When a milestone or subtask pill is hovered, we highlight every day cell
  // in its [start_date, due_date] range — that's the "span on hover" behavior.
  const [hoveredRange, setHoveredRange] = useState<{ startStr: string | null; dueStr: string | null; priority?: string | null; colorRing?: string } | null>(null);

  // Build 9 months of calendar data using LOCAL dates
  const calendarMonths = useMemo(() => {
    type DayMilestoneEntry = { milestone: any; endpoint: "start" | "due" | "both" };
    const months: {
      key: string;
      year: number;
      monthName: string;
      days: (null | {
        day: number;
        date: Date;
        localStr: string;
        milestones: DayMilestoneEntry[];
        tasks: any[];
        holidays: { name: string }[];
      })[];
    }[] = [];

    let cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    for (let i = 0; i < 9; i++) {
      const year = cur.getFullYear();
      const month = cur.getMonth();
      const holidays = getHolidaysForYear(year);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDow = new Date(Date.UTC(year, month, 1)).getUTCDay();
      const days: typeof months[0]["days"] = [];

      for (let j = 0; j < firstDow; j++) days.push(null);

      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(Date.UTC(year, month, d, 12, 0, 0));
        const localStr = toLocalDateStr(dateObj);

        // Anchor each milestone on its START date (or due date if no
        // start). Single pill per milestone, marking when work begins.
        // Hover-range highlight still spans [start, due] so the visible
        // duration is preserved across the calendar grid.
        const dayMilestones: DayMilestoneEntry[] = [];
        for (const m of milestones as any[]) {
          const startStr = m.startDate ? toLocalDateStr(m.startDate) : null;
          const dueStr = m.dueDate ? toLocalDateStr(m.dueDate) : null;
          const anchorStr = startStr ?? dueStr;
          if (!anchorStr || anchorStr !== localStr) continue;
          // "endpoint" tag retained for downstream hover-range styling:
          // "both" when start === due (single-day milestone), else "start".
          const endpoint: "start" | "both" =
            startStr && dueStr && startStr === dueStr ? "both" : "start";
          dayMilestones.push({ milestone: m, endpoint });
        }

        // Subtask pills render on the due date (or start if no due). Same
        // compact-by-default / hover-to-see-span pattern as milestones.
        const dayTasks = (milestones as any[]).flatMap((m) => (m.tasks || []) as any[]).filter((t: any) => {
          if (t.dueDate) return toLocalDateStr(t.dueDate) === localStr;
          if (t.startDate) return toLocalDateStr(t.startDate) === localStr;
          return false;
        });

        const dayHolidays = holidays.filter(
          (h) => h.date.getFullYear() === year && h.date.getMonth() === month && h.date.getDate() === d
        );
        days.push({
          day: d,
          date: dateObj,
          localStr,
          milestones: dayMilestones,
          tasks: dayTasks,
          holidays: dayHolidays,
        });
      }

      months.push({
        key: `${year}-${month}`,
        year,
        monthName: cur.toLocaleString("default", { month: "long" }),
        days,
      });
      cur = new Date(year, month + 1, 1);
    }
    return months;
  }, [startDate, milestones]);

  const handleDrop = (e: React.DragEvent, dateObj: Date) => {
    e.preventDefault();
    if (draggedId) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      updateMilestone.mutate({
        milestoneId: draggedId,
        dueDate: `${y}-${m}-${d}T12:00:00`,
      });
    }
    setDraggedId(null);
  };

  const handleCreateTask = () => {
    if (!newTaskDate || !newTaskName.trim()) return;
    const y = newTaskDate.getFullYear();
    const m = String(newTaskDate.getMonth() + 1).padStart(2, "0");
    const d = String(newTaskDate.getDate()).padStart(2, "0");
    createMilestone.mutate({
      name: newTaskName.trim(),
      dueDate: `${y}-${m}-${d}T12:00:00`,
    });
    setNewTaskDate(null);
    setNewTaskName("");
  };

  const handleSaveDetail = () => {
    if (!detailModal) return;
    updateNote.mutate({ milestoneId: detailModal.id, note: tempNote });
    if (localProgress !== null) {
      updateMilestone.mutate({ milestoneId: detailModal.id, progress: localProgress });
    }
    setDetailModal(null);
    setLocalProgress(null);
  };

  const openDetail = (m: ProjectMilestone) => {
    setDetailModal(m);
    setTempNote(calendarNotes[m.id] || "");
    setLocalProgress(null);
  };

  const todayStr = toLocalDateStr(new Date());

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
        <span>Colors identify milestones.</span>
        <span>▸ start · ◆ due</span>
        {!clientFacing && (
          <span className="flex items-center gap-2">
            Priority:
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> High</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Medium</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Low</span>
          </span>
        )}
        <span className="opacity-60">· hover a milestone to see its date range</span>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {calendarMonths.map((month) => {
          // Collect unique milestones for this month (start dates only, no spanning dupes)
          const monthMilestones: ProjectMilestone[] = [];
          const seen = new Set<string>();
          month.days.forEach((d) => {
            if (d) d.milestones.forEach(({ milestone }) => { if (!seen.has(milestone.id)) { seen.add(milestone.id); monthMilestones.push(milestone); } });
          });

          return (
            <Card key={month.key} className="overflow-hidden">
              <div className="px-4 py-2.5 border-b bg-muted/30">
                <h3 className="font-semibold text-sm">
                  {month.monthName} <span className="text-muted-foreground font-normal">{month.year}</span>
                </h3>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 text-center text-[11px]">
                {WEEKDAYS.map((d) => (
                  <div key={d} className="py-1.5 font-semibold text-muted-foreground border-b bg-muted/20">{d}</div>
                ))}
                {month.days.map((dayObj, idx) => {
                  if (!dayObj) return <div key={`e-${idx}`} className="h-[72px]" />;
                  const isToday = dayObj.localStr === todayStr;
                  const hasHoliday = dayObj.holidays.length > 0;
                  const isWeekend = dayObj.date.getDay() === 0 || dayObj.date.getDay() === 6;

                  // Hover-to-span: when a pill is hovered, highlight every day in its range.
                  const inHoverRange = !!(hoveredRange && (
                    (hoveredRange.startStr || hoveredRange.dueStr) &&
                    dayObj.localStr >= (hoveredRange.startStr || hoveredRange.dueStr || "") &&
                    dayObj.localStr <= (hoveredRange.dueStr || hoveredRange.startStr || "")
                  ));
                  const hoverTint = !inHoverRange
                    ? ""
                    : cn(
                        "ring-1 ring-inset",
                        hoveredRange?.colorRing || "ring-primary/40",
                        "bg-muted/30",
                      );

                  return (
                    <div
                      key={`d-${dayObj.day}`}
                      className={cn(
                        "h-[72px] border-b border-r p-0.5 relative group transition-colors overflow-hidden",
                        !inHoverRange && isToday ? "bg-primary/5 ring-1 ring-inset ring-primary/30" :
                        !inHoverRange && isWeekend ? "bg-muted/20" : "",
                        !inHoverRange && "hover:bg-muted/10",
                        hoverTint,
                      )}
                      onDragOver={clientFacing ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                      onDrop={clientFacing ? undefined : (e) => handleDrop(e, dayObj.date)}
                    >
                      <div className="flex items-center justify-between px-0.5">
                        {!clientFacing && (
                          <button
                            className="w-4 h-4 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary-text transition-opacity"
                            onClick={() => { setNewTaskDate(dayObj.date); setNewTaskName(""); }}
                          >+</button>
                        )}
                        {clientFacing && <span className="w-4 h-4" />}
                        <span className={cn(
                          "text-[11px] font-medium",
                          isToday && "bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-[10px]",
                          hasHoliday && !isToday && "text-blue-400"
                        )}>{dayObj.day}</span>
                      </div>
                      <div className="space-y-0.5 mt-0.5">
                        {dayObj.holidays.map((h, i) => (
                          <div key={`h-${i}`} className="text-[8px] bg-blue-500/15 text-blue-400 rounded px-1 truncate font-medium">{h.name}</div>
                        ))}
                        {(dayObj.milestones as any[]).map((entry) => {
                          const m = entry.milestone;
                          const startStr = m.startDate ? toLocalDateStr(m.startDate) : null;
                          const dueStr = m.dueDate ? toLocalDateStr(m.dueDate) : null;
                          const progress = typeof m.progress === "number" ? m.progress : 0;
                          const color = identityColor(m.id);
                          // Endpoint marker: arrow for start, flag for due, none for single-day.
                          const endpointGlyph = entry.endpoint === "start" ? "▸" : entry.endpoint === "due" ? "◆" : null;
                          const endpointLabel = entry.endpoint === "start" ? "starts" : entry.endpoint === "due" ? "due" : "";
                          const pretty = (d: string | null) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "?";
                          const tooltip = `${m.name} · ${pretty(startStr)} → ${pretty(dueStr)} · ${progress}%${endpointLabel ? ` · ${endpointLabel} this day` : ""}`;
                          return (
                            <div
                              key={`ms-${m.id}-${entry.endpoint}`}
                              className={cn(
                                "text-[10px] rounded px-1 truncate cursor-pointer border font-medium leading-tight flex items-center gap-1",
                                color.bg, color.text, color.border,
                                progress === 100 && "opacity-60 line-through"
                              )}
                              draggable={!clientFacing && entry.endpoint !== "start"}
                              onDragStart={clientFacing ? undefined : (e) => { if (entry.endpoint !== "start") { setDraggedId(m.id); e.dataTransfer.effectAllowed = "move"; } }}
                              onClick={clientFacing ? undefined : (e) => { e.stopPropagation(); openDetail(m); }}
                              onMouseEnter={() => setHoveredRange({ startStr, dueStr, priority: m.priority, colorRing: color.ring })}
                              onMouseLeave={() => setHoveredRange(null)}
                              title={tooltip}
                            >
                              {!clientFacing && <span className={cn("w-1 h-1 rounded-full flex-shrink-0", priorityDot(m.priority))} />}
                              {endpointGlyph && <span className="opacity-60 text-[9px]">{endpointGlyph}</span>}
                              <span className="truncate">{m.name}</span>
                              {progress > 0 && progress < 100 && (
                                <span className="opacity-60 ml-auto text-[9px]">{progress}%</span>
                              )}
                            </div>
                          );
                        })}
                        {!clientFacing && (dayObj as any).tasks?.slice(0, 2).map((t: any) => {
                          const statusClass =
                            t.status === "done" ? "bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400"
                            : t.status === "in_progress" ? "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400"
                            : "bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400";
                          const startStr = t.startDate ? toLocalDateStr(t.startDate) : null;
                          const dueStr = t.dueDate ? toLocalDateStr(t.dueDate) : null;
                          const pct = typeof t.percentComplete === "number" ? t.percentComplete : 0;
                          const pretty = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "?";
                          const tooltip = `${t.name} · ${pretty(t.startDate)} → ${pretty(t.dueDate)} · ${pct}%${t.assigneeName ? ` · ${t.assigneeName}` : ""}`;
                          return (
                            <div
                              key={t.id}
                              className={cn(
                                "text-[9px] font-medium border rounded px-1 truncate leading-tight cursor-default",
                                statusClass,
                                t.status === "done" && "line-through opacity-70"
                              )}
                              title={tooltip}
                              onMouseEnter={() => setHoveredRange({ startStr, dueStr, priority: null })}
                              onMouseLeave={() => setHoveredRange(null)}
                            >
                              {t.name}
                            </div>
                          );
                        })}
                        {!clientFacing && ((dayObj as any).tasks?.length || 0) > 2 && (
                          <div className="text-[8px] text-muted-foreground px-1">
                            +{(dayObj as any).tasks.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Agenda */}
              {monthMilestones.length > 0 && (
                <div className="border-t">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="text-left px-3 py-1.5 font-semibold">Task</th>
                        <th className="text-left px-2 py-1.5 font-semibold w-16">Days</th>
                        <th className="text-left px-2 py-1.5 font-semibold w-24">Due</th>
                        <th className="text-right px-3 py-1.5 font-semibold w-14">Done</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthMilestones.map((m) => (
                        <tr key={m.id} className={cn("border-b last:border-0 hover:bg-muted/20", !clientFacing && "cursor-pointer")} onClick={clientFacing ? undefined : () => openDetail(m)}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              {!clientFacing && <div className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", priorityDot(m.priority))} />}
                              <span className={cn("truncate", (m.progress || 0) === 100 && "line-through text-muted-foreground")}>{m.name}</span>
                            </div>
                            {!clientFacing && calendarNotes[m.id] && (
                              <p className="text-muted-foreground truncate mt-0.5 pl-3 text-[10px]">{calendarNotes[m.id]}</p>
                            )}
                          </td>
                          <td className="px-2 py-2 text-muted-foreground">{m.duration || 1}d</td>
                          <td className="px-2 py-2 text-muted-foreground">
                            {m.dueDate && new Date(m.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className={cn("font-medium", (m.progress || 0) === 100 ? "text-green-500" : "text-muted-foreground")}>
                              {m.progress || 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* ─── Milestone Detail Modal ─── */}
      {!clientFacing && <Dialog open={!!detailModal} onOpenChange={(open) => { if (!open) { setDetailModal(null); setLocalProgress(null); } }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-lg">{detailModal?.name}</DialogTitle>
          </DialogHeader>
          {detailModal && (() => {
            const displayProgress = localProgress !== null ? localProgress : (detailModal.progress || 0);
            const isComplete = displayProgress === 100;
            return (
              <div className="space-y-5">
                {/* Key info cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border p-3 text-center">
                    <Calendar className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                    <p className="text-xs text-muted-foreground">Date Range</p>
                    <p className="text-sm font-medium mt-0.5">
                      {detailModal.dueDate
                        ? formatDateRange(detailModal.dueDate, detailModal.duration || 1)
                        : "Not set"}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3 text-center">
                    <Clock className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-medium mt-0.5">{detailModal.duration || 1} day{(detailModal.duration || 1) > 1 ? "s" : ""}</p>
                  </div>
                  <div className="rounded-lg border p-3 text-center">
                    <User className="w-4 h-4 mx-auto text-muted-foreground mb-1" />
                    <p className="text-xs text-muted-foreground">Owner</p>
                    <p className="text-sm font-medium mt-0.5">{detailModal.owner || "TEAM"}</p>
                  </div>
                </div>

                {/* Priority */}
                <div className="flex items-center gap-2">
                  <Label className="text-xs w-16">Priority</Label>
                  <Badge className={cn("text-xs", priorityColor(detailModal.priority))}>{detailModal.priority || "Medium"}</Badge>
                </div>

                {/* Progress — editable */}
                <div className={cn(
                  "rounded-lg border p-4 space-y-3",
                  isComplete ? "border-green-500/30 bg-green-500/5" : ""
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-2xl font-bold", isComplete ? "text-green-500" : "")}>
                        {displayProgress}%
                      </span>
                      {isComplete && <Badge className="bg-green-500 text-white text-[10px]">Complete</Badge>}
                    </div>
                    {displayProgress < 100 ? (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1 h-7 text-xs"
                        onClick={() => setLocalProgress(100)}>
                        <Check className="w-3 h-3" /> Mark Complete
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="gap-1 h-7 text-xs"
                        onClick={() => setLocalProgress(0)}>
                        <X className="w-3 h-3" /> Reopen
                      </Button>
                    )}
                  </div>
                  <Slider
                    value={[displayProgress]}
                    onValueChange={([v]) => setLocalProgress(v)}
                    min={0} max={100} step={5}
                  />
                  <div className="flex justify-between text-[9px] text-muted-foreground">
                    <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Notes</Label>
                  <Textarea
                    value={tempNote}
                    onChange={(e) => setTempNote(e.target.value)}
                    placeholder="Add context, call logs, or notes for this milestone..."
                    rows={3}
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailModal(null); setLocalProgress(null); }}>Cancel</Button>
            <Button onClick={handleSaveDetail}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* New Task Modal */}
      {!clientFacing && <Dialog open={!!newTaskDate} onOpenChange={(open) => !open && setNewTaskDate(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Scheduling for:{" "}
            <strong>{newTaskDate?.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong>
          </p>
          <Input
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            placeholder="Task name..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateTask();
              if (e.key === "Escape") setNewTaskDate(null);
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTaskDate(null)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={!newTaskName.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}
    </div>
  );
}
